import { signatureRequestsApi } from "@api";
import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import { SignatureRequest } from "@wingspanhq/wingspan-api-sdk";
import { useWSMutation } from "@ws-react-query";

import {
  helloSignClient,
  helloSignPromise
} from "../../../../../utils/helloSign";
import { queriesSignatureRequests } from "../../../../../v3/query/queriesSignatureRequests";
import { getRequirementsDependencies } from "../../../dependencies";

/**
 * Signs a signature request in the embedded HelloSign client. Replaces the
 * v1 flow that read the signing url off the signed document's signature
 * matching the session user.
 */
export const useSignSignatureRequest = () => {
  const { openSnackbar } = useWSSnackbar();

  return useWSMutation(
    async (signatureRequest: SignatureRequest) => {
      const signingUrls =
        await signatureRequestsApi.getSignatureRequestSigningUrls({
          requestId: signatureRequest.id!
        });

      // TODO(v3-verify): confirm v3 signing URLs are HelloSign-embeddable at
      // runtime and that the endpoint scopes the returned entries to the
      // caller — the first entry carrying a url is treated as the current
      // signer.
      const signingUrl = signingUrls.signers?.find(
        signer => signer.signingUrl
      )?.signingUrl;

      if (!signingUrl) {
        openSnackbar({
          icon: {
            name: "alert"
          },
          type: "error",
          message: "No sign url on a document"
        });
        throw new Error("No sign url on a document");
      }

      helloSignClient.open(signingUrl);

      const hsStatus = await helloSignPromise();
      if (hsStatus === "signed") {
        openSnackbar({
          icon: {
            name: "check"
          },
          message: "Document signed"
        });
      }
    },
    {
      dependencies: getRequirementsDependencies(),
      // Awaited, not just invalidated: the signature request document backs the
      // status pill and progress steps, so resolving before it is re-read leaves
      // the screen on "awaiting signature" after a successful signing.
      //
      // NOTE: HelloSign reports "signed" to the client before the backend has
      // finished processing its own webhook, so this re-read can still legitimately
      // return the pre-signature status. Closing that window needs the backend to
      // make the transition observable (or a bounded poll); awaiting removes the
      // client-side half of the race only.
      //
      // Exact key only: `awaitDependencies` resolves through `refetchQueries`,
      // which is prefix-matching and includes inactive queries.
      awaitDependencies: (_result, signatureRequest: SignatureRequest) => [
        queriesSignatureRequests.getSignatureRequest.getQueryKey({
          requestId: signatureRequest.id!
        })
      ]
    }
  );
};
